import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { cn, Icon, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { ClearFiltersButton } from "@/components/ClearFiltersButton"
import { FiltersToggleButton } from "@/components/FiltersToggleButton"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"
import {
  countActiveOpportunityFilters,
  OPPORTUNITY_FILTER_ALL,
} from "../../../constants/opportunity-filters"

const SEARCH_DEBOUNCE_MS = 300

function isInputFocused(): boolean {
  const tag = document.activeElement?.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (document.activeElement as HTMLElement)?.isContentEditable === true
}

export interface FilterOption {
  value: string
  label: string
}

export interface OpportunitiesFilters {
  action: string
  dataset: string
  feature: string
  subFeature: string
  engine: string
}

export interface OpportunitiesTableToolbarProps {
  search: string
  onSearchChange: (value: string) => void
  filters: OpportunitiesFilters
  onFilterChange: (key: keyof OpportunitiesFilters, value: string) => void
  /** When false, the facet filter panel is not shown (caller lacks OPPORTUNITIES_FACETS). */
  showFacetFilters?: boolean
  onClearAll?: () => void
  actionOptions: FilterOption[]
  datasetOptions: FilterOption[]
  featureOptions: FilterOption[]
  subFeatureOptions: FilterOption[]
  engineOptions: FilterOption[]
  /** Renders inline after search/filters on the left side of the toolbar row. */
  filterRowAddon?: React.ReactNode
  children?: React.ReactNode
}

function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
  tooltip,
  defaultValue = OPPORTUNITY_FILTER_ALL,
}: {
  value: string
  onValueChange: (v: string) => void
  placeholder: string
  options: FilterOption[]
  tooltip?: string
  defaultValue?: string
}) {
  const isActive = value !== defaultValue

  const trigger = (
    <SelectTrigger
      className={cn(
        "h-9 w-full min-w-0 text-xs transition-colors sm:h-8 sm:w-[180px]",
        isActive && "border-primary/40 bg-primary/5 text-foreground dark:border-primary/60 dark:bg-primary/15 dark:text-primary",
      )}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  )

  return (
    <Select value={value} onValueChange={onValueChange}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function OpportunitiesTableToolbar({
  search,
  onSearchChange,
  filters,
  onFilterChange,
  showFacetFilters = true,
  onClearAll,
  actionOptions,
  datasetOptions,
  featureOptions,
  subFeatureOptions,
  engineOptions,
  filterRowAddon,
  children,
}: OpportunitiesTableToolbarProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState(search)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { debounced: debouncedSearch, cancel } = useDebouncedCallback(
    onSearchChange,
    SEARCH_DEBOUNCE_MS,
  )

  useEffect(() => {
    setInputValue(search)
    cancel()
  }, [search, cancel])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "/" && !isInputFocused()) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const selectFilterCount = countActiveOpportunityFilters(filters)
  const [filtersOpen, setFiltersOpen] = useState(selectFilterCount > 0)

  useEffect(() => {
    if (selectFilterCount > 0) setFiltersOpen(true)
  }, [selectFilterCount])

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-2">
      {/* Stack below lg; side-by-side only when action controls fit (~1024px+) */}
      <div className="grid min-w-0 max-w-full grid-cols-1 items-start gap-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-3">
        <div className="flex min-w-0 flex-wrap items-center justify-start gap-2 overflow-hidden">
          <div className="relative w-full min-w-0 max-w-sm flex-[1_1_12rem] sm:w-64 sm:max-w-none sm:flex-none">
            <Input
              ref={searchInputRef}
              data-testid="opportunities-table-search"
              iconStart="search"
              placeholder={t("opportunities.table.searchPlaceholder")}
              value={inputValue}
              onChange={(e) => {
                const next = e.target.value
                setInputValue(next)
                debouncedSearch(next)
              }}
              className={cn("h-9 text-sm sm:h-8", inputValue ? "pr-8" : "pr-9")}
            />
            {!inputValue && (
              <kbd aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-block">/</kbd>
            )}
            {inputValue && (
              <button
                type="button"
                data-testid="opportunities-table-search-clear"
                onClick={() => {
                  cancel()
                  setInputValue("")
                  onSearchChange("")
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t("common.clear")}
              >
                <Icon name="x" size="xs" />
              </button>
            )}
          </div>

          {showFacetFilters && (
            <>
              <FiltersToggleButton
                isOpen={filtersOpen}
                activeCount={selectFilterCount}
                onToggle={() => setFiltersOpen((prev) => !prev)}
                panelId="opportunities-filter-panel"
              />

              {selectFilterCount > 0 && onClearAll && (
                <ClearFiltersButton onClear={onClearAll} />
              )}
            </>
          )}

          {filterRowAddon}
        </div>

        {children != null && (
          <div className="flex min-w-0 max-w-full flex-wrap items-center justify-start gap-2 lg:flex-nowrap lg:justify-end [&>*]:max-w-full">
            {children}
          </div>
        )}
      </div>

      {/* Collapsible filter row */}
      {showFacetFilters && filtersOpen && (
        <TooltipProvider delayDuration={400}>
        <div
          id="opportunities-filter-panel"
          className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-1 duration-150 sm:flex sm:flex-wrap sm:items-center sm:gap-2"
        >
          <FilterSelect
            value={filters.action}
            onValueChange={(v) => onFilterChange("action", v)}
            placeholder={t("opportunities.filters.allActions")}
            options={actionOptions}
            tooltip={t("opportunities.filters.actionTooltip")}
          />
          <FilterSelect
            value={filters.dataset}
            onValueChange={(v) => onFilterChange("dataset", v)}
            placeholder={t("opportunities.filters.allFlowNames")}
            options={datasetOptions}
            tooltip={t("opportunities.filters.flowNameTooltip")}
          />
          <FilterSelect
            value={filters.feature}
            onValueChange={(v) => onFilterChange("feature", v)}
            placeholder={t("opportunities.filters.allFeatures")}
            options={featureOptions}
            tooltip={t("opportunities.filters.featureTooltip")}
          />
          <FilterSelect
            value={filters.subFeature}
            onValueChange={(v) => onFilterChange("subFeature", v)}
            placeholder={t("opportunities.filters.allSubFeatures")}
            options={subFeatureOptions}
            tooltip={t("opportunities.filters.subFeatureTooltip")}
          />
          <FilterSelect
            value={filters.engine}
            onValueChange={(v) => onFilterChange("engine", v)}
            placeholder={t("opportunities.filters.allEngines")}
            options={engineOptions}
            tooltip={t("opportunities.filters.engineTooltip")}
          />
        </div>
        </TooltipProvider>
      )}
    </div>
  )
}
